import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface HeroSectionProps {
  title: string;
  accentWord?: string;
  accentColor?: string;
  description: string;
  secondaryDescription?: string;
  image: string;
  secondaryImage?: string;
  blobColor: string;
  blobStyle?: "diagonal" | "blob" | "gradient";
  ctaLink: string;
  rating?: number;
  reviewCount?: string;
  isLast?: boolean;
}

export function HeroSection({
  title,
  accentWord,
  accentColor = "#c0392b",
  description,
  secondaryDescription,
  image,
  secondaryImage,
  blobColor,
  blobStyle = "blob",
  ctaLink,
  rating = 4.9,
  reviewCount = "500+ Customer Rating",
  isLast = false,
}: HeroSectionProps) {
  const words = title.split(" ");

  const renderTitle = () => {
    if (accentWord) {
      return (
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none">
          {words.map((word, i) =>
            word === accentWord ? (
              <span key={i} style={{ color: accentColor }}>
                {word}
                {i < words.length - 1 ? " " : ""}
              </span>
            ) : (
              <span key={i}>{word}{i < words.length - 1 ? " " : ""}</span>
            )
          )}
        </h1>
      );
    }
    // Break title into lines: every ~2 words per line
    const lines: string[] = [];
    let line: string[] = [];
    words.forEach((word, i) => {
      line.push(word);
      if (line.length === 2 || i === words.length - 1) {
        lines.push(line.join(" "));
        line = [];
      }
    });
    return (
      <h1 className="text-5xl md:text-6xl lg:text-[4.5rem] font-black tracking-tight leading-[1.0]">
        {lines.map((l, i) => (
          <span key={i} className="block">{l}</span>
        ))}
      </h1>
    );
  };

  return (
    <section className="w-full bg-white">
      {/* 3-column grid matching the screenshot */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] min-h-[580px]">

        {/* LEFT: Text content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col justify-between px-8 lg:px-12 xl:px-16 py-12 lg:py-16"
        >
          <div className="space-y-6">
            {renderTitle()}

            <p className="text-sm md:text-base text-gray-500 leading-relaxed max-w-xs">
              {description}
            </p>

            <div>
              <Button
                asChild
                className="h-11 px-7 text-xs font-bold uppercase tracking-widest bg-black text-white hover:bg-black/90 rounded-none"
              >
                <Link to={ctaLink}>Shop Now</Link>
              </Button>
            </div>
          </div>

          {/* Rating at bottom left */}
          <div className="flex items-end gap-3 mt-10">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-black font-mono">{rating}</span>
            </div>
            <div className="flex flex-col mb-1">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className="w-3 h-3 fill-black text-black" />
                ))}
              </div>
              <span className="text-xs text-gray-400 mt-0.5">{reviewCount}</span>
            </div>
          </div>
        </motion.div>

        {/* CENTER: Product image on colored background */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          className="relative flex items-center justify-center w-full lg:w-[400px] xl:w-[460px] overflow-hidden"
          style={{ minHeight: "440px" }}
        >
          {blobStyle === "diagonal" ? (
            /* Diagonal cut background - matches screenshot exactly */
            <div className="absolute inset-0" style={{ background: blobColor }}>
              {/* Diagonal white clip on right side */}
              <div
                className="absolute inset-0 bg-white"
                style={{
                  clipPath: "polygon(75% 0%, 100% 0%, 100% 100%, 60% 100%)",
                }}
              />
            </div>
          ) : blobStyle === "gradient" ? (
            <div
              className="absolute inset-0"
              style={{ background: blobColor }}
            />
          ) : (
            /* Blob shape */
            <div
              className="absolute"
              style={{
                width: "88%",
                height: "88%",
                top: "6%",
                left: "6%",
                backgroundColor: blobColor,
                borderRadius: "60% 40% 55% 45% / 50% 60% 40% 50%",
              }}
            />
          )}
          <img
            src={image}
            alt={title}
            className="relative z-10 object-contain"
            style={{
              height: "380px",
              maxWidth: "300px",
              filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.15))",
            }}
          />
        </motion.div>

        {/* RIGHT: Secondary content */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          className="flex flex-col justify-between px-8 lg:px-10 xl:px-12 py-12 lg:py-16"
        >
          <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
            {secondaryDescription || description}
          </p>

          <div className="flex flex-col items-end gap-6">
            {secondaryImage && (
              <img
                src={secondaryImage}
                alt="secondary product"
                className="w-20 h-20 object-contain"
                style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.12))" }}
              />
            )}

            {/* Scroll down indicator */}
            {!isLast && (
              <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors cursor-pointer">
                <ChevronDown className="w-4 h-4" />
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Bottom border separator */}
      <div className="h-px bg-gray-100 mx-8" />
    </section>
  );
}
